/**
 * Effective permissions, grant rules and legacy role mappings (SC-439).
 *
 * Pure: plain data in, plain data out, no imports beyond the catalogue. The server (routes and the
 * Colyseus join), the client (UI gating) and a hand-copied cloud version all run the same rules,
 * so they can't disagree on who may do what. The platform-admin bypass is NOT here: it lives at
 * each enforcement site, so these rules stay the company's own.
 *
 * Role keys and permission keys arrive as plain strings (request bodies, Parse rows). A key that
 * is not in the catalogue never counts as held; asking to grant one is refused.
 */

import { OWNER_ROLE_KEY, PERMISSION_CATALOGUE, getPermissionEntry } from './catalogue'
import type { PermissionKey, SystemRoleKey } from './catalogue'

export interface PermissionOverride {
  key: string
  /** true = allow, false = deny. */
  allowed: boolean
}

export interface EffectivePermissionsInput {
  roleKey: string
  /** The role's own permission list (for a system role, `SYSTEM_ROLE_PERMISSIONS[roleKey]`). */
  rolePermissions: readonly string[]
  overrides?: readonly PermissionOverride[]
  /** Keys whose `Permission.isActive` is false. */
  retiredKeys?: readonly string[]
}

/**
 * What a member may do in a company: role permissions, plus allows, minus denies, in catalogue
 * order so the result is stable.
 *
 * - The Owner has every key and no override changes that, so a deny can't lock the company out of
 *   its own settings.
 * - A deny beats a role and an allow, whatever the order of the overrides.
 * - Owner-only keys are dropped for everyone else even if a role or an allow lists them, so a
 *   custom role or a bad write can't hand out ownership powers.
 * - A retired key is dropped for everyone, Owner included.
 */
export function effectivePermissions(input: EffectivePermissionsInput): PermissionKey[] {
  const retired = input.retiredKeys || []
  const live = PERMISSION_CATALOGUE.filter((e) => !retired.includes(e.key))
  if (input.roleKey === OWNER_ROLE_KEY) return live.map((e) => e.key)

  const held = new Set<string>(input.rolePermissions || [])
  const overrides = input.overrides || []
  for (const o of overrides) if (o.allowed) held.add(o.key)
  for (const o of overrides) if (!o.allowed) held.delete(o.key)
  return live.filter((e) => e.grantable && held.has(e.key)).map((e) => e.key)
}

/** A catalogue key that is not owner-only and not retired: the only kind anyone can be given. */
function isLiveGrantable(key: string, retiredKeys?: readonly string[]): boolean {
  const entry = getPermissionEntry(key)
  return !!entry && entry.grantable && !(retiredKeys || []).includes(key)
}

/**
 * Granting onward is limited to what the granter holds, so no edit can raise anyone above the
 * person making it. "Holds" includes allow overrides because `actorPermissions` is the effective
 * set.
 */
export function canGrantPermission(
  actorPermissions: readonly string[],
  key: string,
  retiredKeys?: readonly string[]
): boolean {
  return isLiveGrantable(key, retiredKeys) && (actorPermissions || []).includes(key)
}

export interface RoleRef {
  key: string
  permissions: readonly string[]
}

/** First grantable, live key of the role that the actor does not hold, in catalogue order. */
function firstUnheldInRole(
  actorPermissions: readonly string[],
  role: RoleRef,
  retiredKeys?: readonly string[]
): string | null {
  const unheld = PERMISSION_CATALOGUE.find(
    (e) =>
      (role.permissions || []).includes(e.key) &&
      isLiveGrantable(e.key, retiredKeys) &&
      !(actorPermissions || []).includes(e.key)
  )
  return unheld ? unheld.key : null
}

/**
 * Assigning a role grants every permission in it, so the actor must hold all of them. Owner-only
 * and retired keys in the role don't count against the actor (nobody can hold them). The `owner`
 * role is never assignable here: Owners change only through a transfer or the platform
 * owner-management routes, so no member's access change can create or remove one.
 * Whether the actor may assign roles at all (`roles.assign`) is `checkAccessChange`'s call.
 */
export function canAssignRole(
  actorPermissions: readonly string[],
  role: RoleRef,
  retiredKeys?: readonly string[]
): boolean {
  return (
    role.key !== OWNER_ROLE_KEY && firstUnheldInRole(actorPermissions, role, retiredKeys) === null
  )
}

export type AccessRefusalReason =
  'missing_permission' | 'not_held' | 'target_holds_more' | 'self_edit' | 'owner_target'

/** The body the server and cloud return with a 403 / thrown error. */
export interface AccessRefusal {
  error: 'forbidden'
  reason: AccessRefusalReason
  /**
   * `missing_permission`: the key the actor needs. `not_held`: the key the actor can't grant (also
   * for owner-only, retired and unknown keys, and for the `owner` role, which is refused as
   * `workspace.transfer_ownership`: use the transfer). `target_holds_more`: the first key of the
   * target's current access that the actor lacks, so the person can't be edited at all, whatever
   * the edit is.
   */
  permission?: string
}

export type AccessChangeResult = { ok: true } | { ok: false; refusal: AccessRefusal }

export interface AccessChangeInput {
  actorId: string
  /** The actor's effective permissions in this company, from `effectivePermissions`. */
  actorPermissions: readonly string[]
  targetId: string
  /** The target's current role and its permissions, read from the database, never the request. */
  targetRole: RoleRef
  /** The target's current overrides, read from the database. */
  targetOverrides?: readonly PermissionOverride[]
  change: {
    /** The role to give the target, with its permissions. The target's current key = no change. */
    role?: RoleRef
    /**
     * The full override set the target should end up with: an entry not listed is removed, and
     * `[]` removes them all. Leave it out to keep the current overrides. Only the difference from
     * `targetOverrides` is judged, so resending the current set unchanged is not an edit.
     */
    overrides?: readonly PermissionOverride[]
  }
  retiredKeys?: readonly string[]
}

function refuse(reason: AccessRefusalReason, permission?: string): AccessChangeResult {
  const refusal: AccessRefusal = { error: 'forbidden', reason }
  if (permission) refusal.permission = permission
  return { ok: false, refusal }
}

/**
 * Overrides as one state per key (true = allow, false = deny). A deny beats an allow for the same
 * key, exactly as in `effectivePermissions`, so a duplicate entry can't mean one thing here and
 * another there. Exported so the server's access writes read stored overrides by this rule too,
 * instead of keeping a copy that could drift.
 */
export function overrideState(overrides?: readonly PermissionOverride[]): Map<string, boolean> {
  const state = new Map<string, boolean>()
  for (const o of overrides || []) {
    if (!o.allowed) state.set(o.key, false)
    else if (!state.has(o.key)) state.set(o.key, true)
  }
  return state
}

/**
 * Can this actor change this member's role and/or overrides? Checked in order: the actor may do
 * this kind of edit at all, then the target, then the values. Putting the permission first means
 * someone without it learns nothing about the target (such as who the Owner is). The edit is
 * all or nothing: the first refusal wins and nothing is applied.
 *
 * Nobody edits their own access (an Admin can't promote themselves) or an Owner's (only a
 * transfer or a platform admin changes that). Nor does anyone edit a member who holds a key they
 * don't: the target's current access (role, plus their allows, minus their denies, live
 * grantable keys only) must be held by the actor, so a Member delegated `roles.assign` or
 * `permissions.manage` can't demote an Admin or take keys from one, and an Admin who lacks any key
 * can't edit a full Admin. An Admin who holds everything an Admin holds still edits other Admins;
 * the refusal is `target_holds_more`, not `not_held` (which is about what is being given). An edit
 * that changes nothing is not judged.
 * Changing a member's department or title is not an access change and is not judged here.
 *
 * Overrides are judged by what changes, so callers can send the whole set (PUT) without a
 * stale entry refusing the edit, and nobody can get back a key they don't hold:
 * - Newly allowed, or flipped from deny to allow: a grant. The key must be grantable, live and
 *   held by the actor.
 * - A deny removed where the resulting role (the new one if the role changes too) gives the key:
 *   a grant too, as the member gets it back. Owner-only and retired keys give nothing back.
 * - A deny added, an allow removed or flipped to deny: access only shrinks, so no holding needed.
 * - Unchanged entries: not judged. Unknown keys: an unknown allow is `not_held`, an unknown deny
 *   is ignored.
 * Any difference at all needs `permissions.manage`; no difference does not.
 */
export function checkAccessChange(input: AccessChangeInput): AccessChangeResult {
  const { targetRole, retiredKeys } = input
  const actorPermissions = input.actorPermissions || []
  const change = input.change || {}
  const newRole = change.role && change.role.key !== targetRole.key ? change.role : null

  const before = overrideState(input.targetOverrides)
  const after = change.overrides ? overrideState(change.overrides) : before
  const changedKeys = [...new Set([...after.keys(), ...before.keys()])].filter(
    (key) => before.get(key) !== after.get(key)
  )
  if (!newRole && changedKeys.length === 0) return { ok: true }

  if (newRole && !actorPermissions.includes('roles.assign')) {
    return refuse('missing_permission', 'roles.assign')
  }
  if (changedKeys.length > 0 && !actorPermissions.includes('permissions.manage')) {
    return refuse('missing_permission', 'permissions.manage')
  }
  if (input.actorId === input.targetId) return refuse('self_edit')
  if (targetRole.key === OWNER_ROLE_KEY) return refuse('owner_target')

  // After the Owner check, which has its own refusal. Lowering or restricting someone is an edit
  // of them as much as raising them is, so what the target holds now is what the actor must hold.
  const targetHolds = effectivePermissions({
    roleKey: targetRole.key,
    rolePermissions: targetRole.permissions,
    overrides: input.targetOverrides,
    retiredKeys,
  })
  const beyondActor = targetHolds.find((key) => !actorPermissions.includes(key))
  if (beyondActor) return refuse('target_holds_more', beyondActor)

  if (newRole) {
    if (newRole.key === OWNER_ROLE_KEY) return refuse('not_held', 'workspace.transfer_ownership')
    const unheld = firstUnheldInRole(actorPermissions, newRole, retiredKeys)
    if (unheld) return refuse('not_held', unheld)
  }

  const roleAfter = (newRole || targetRole).permissions || []
  const ungranted = changedKeys.find((key) => {
    const now = after.get(key)
    const handsBack =
      now === undefined &&
      before.get(key) === false &&
      roleAfter.includes(key) &&
      isLiveGrantable(key, retiredKeys)
    return (now === true || handsBack) && !canGrantPermission(actorPermissions, key, retiredKeys)
  })
  return ungranted ? refuse('not_held', ungranted) : { ok: true }
}

/** The company role strings the old app bundles still send and read. */
export type LegacyRole = 'manager' | 'admin' | 'user'

const LEGACY_ROLE_KEYS: Readonly<Record<string, SystemRoleKey>> = {
  manager: 'admin',
  admin: 'admin',
  moderator: 'member',
  user: 'member',
}

/** `Company.settings.default_role` only maps these two; the migration reports anything else. */
const LEGACY_DEFAULT_ROLE_KEYS: Readonly<Record<string, SystemRoleKey>> = {
  user: 'member',
  manager: 'admin',
}

function lookup(
  map: Readonly<Record<string, SystemRoleKey>>,
  value: unknown
): SystemRoleKey | null {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(map, value)
    ? map[value]
    : null
}

/**
 * A role string from an old app bundle, as a role key; null when unknown. `owner` is unknown on
 * purpose: an old bundle must never be able to make someone the Owner.
 */
export function roleKeyFromLegacyRole(legacyRole: unknown): SystemRoleKey | null {
  return lookup(LEGACY_ROLE_KEYS, legacyRole)
}

/**
 * The legacy string written next to `roleKey`, so old bundles and queries keep working: the
 * Owner reads as `manager` (the top legacy role), Admin as `admin`, and Member and any custom
 * role as `user`, the lowest, so a custom role never looks more powerful to old code than it is.
 */
export function legacyRoleFromRoleKey(roleKey: unknown): LegacyRole {
  if (roleKey === OWNER_ROLE_KEY) return 'manager'
  return roleKey === 'admin' ? 'admin' : 'user'
}

/** `Company.settings.default_role` as a role key; null when it has no mapping. */
export function roleKeyFromLegacyDefaultRole(legacyDefaultRole: unknown): SystemRoleKey | null {
  return lookup(LEGACY_DEFAULT_ROLE_KEYS, legacyDefaultRole)
}
